import { useEffect, useMemo, useRef, useState } from "react"
import type { ComponentType } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import svg from "../../utils/svg"
import { formatCurrency } from "../../utils/utils"
import styles from "./NimbusPaywall.module.scss"

// ---------------------------------------------------------------------------
// "Nimbus" — a FEATURE-SPECIFIC paywall. It sells exactly one thing: turning
// on Encrypted Cloud Backup. The whole screen IS the feature, dramatised: the
// user's camera roll sits in a device tray at the bottom, and — live, on a
// loop — tiles lift off, ride a column of light, and lock into a luminous
// cloud that fills a "secured" ring while a counter ticks up. You are watching
// your library become safe before you've paid a cent.
//
// Nothing here is reused from another paywall. The metaphor (memories rising
// into a protected cloud), the palette (above-the-clouds twilight: brand blue
// deepening to indigo, warm gold as the light of the cloud), and the whole
// lift-and-lock animation are new. Fonts + colour tokens are the house set.
//
// Yearly is the hero: the largest number on screen, per-month broken out, a
// savings badge, the default selection, and the trial timeline built around
// it. Monthly is offered at a deliberately smaller size. Copy is App Store
// compliant and never implies photos are lost when a subscription ends —
// backup is framed as a second copy, and cancelling leaves the library intact.
// ---------------------------------------------------------------------------

const PUB = process.env.PUBLIC_URL ?? ""
const mem = (n: number) => `${PUB}/nimbus/mem${n}.jpg`

// The camera roll shown in the device tray at the bottom of the stage.
const TRAY = [2, 5, 11, 1, 7, 9, 4, 13, 6, 14, 8, 3]

// The tiles that continuously rise from the tray into the cloud. Each carries
// a start/end horizontal offset (so paths fan out then converge on the cloud),
// a delay, and a per-flight duration for an organic, non-mechanical feel.
type Flyer = { n: number; x0: number; x1: number; d: number; dur: number }
const FLYERS: Flyer[] = [
  { n: 2, x0: -96, x1: -6, d: 0.0, dur: 4.0 },
  { n: 7, x0: 78, x1: 8, d: 0.65, dur: 4.4 },
  { n: 11, x0: -34, x1: 2, d: 1.25, dur: 3.8 },
  { n: 13, x0: 108, x1: 10, d: 1.9, dur: 4.6 },
  { n: 5, x0: -118, x1: -8, d: 2.5, dur: 4.2 },
  { n: 9, x0: 40, x1: 4, d: 3.15, dur: 4.0 },
  { n: 1, x0: -70, x1: -3, d: 3.8, dur: 4.5 },
]

type Feature = {
  key: string
  Icon: ComponentType<{ className?: string }>
  color: string
  title: string
  desc: string
}

// Repo feature icons, reused here and tinted to the Nimbus palette.
const FEATURES: Feature[] = [
  {
    key: "auto",
    Icon: svg.featureIcons.CloudVault,
    color: "#7fb2ff",
    title: "Backs up the instant you shoot",
    desc: "Every new photo and video is copied off your phone automatically — no button, no waiting.",
  },
  {
    key: "encrypted",
    Icon: svg.featureIcons.SecurityPlus,
    color: "#ffc06b",
    title: "End-to-end encrypted",
    desc: "Locked with a key only you hold. Not us, not anyone — your memories stay yours.",
  },
  {
    key: "devices",
    Icon: svg.featureIcons.Wifi,
    color: "#6fe3c4",
    title: "On every device you own",
    desc: "Full-resolution originals, in sync across phone, tablet and the web in seconds.",
  },
  {
    key: "recover",
    Icon: svg.featureIcons.Trash,
    color: "#ff9db4",
    title: "Undo any delete",
    desc: "Restore a photo — or a whole album — long after it left your camera roll.",
  },
]

function NimbusPaywall() {
  const { state } = useAppState()
  const reduce = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : ""
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : ""
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : ""

  const savings = useMemo(() => {
    if (!yearlyProduct || !monthlyProduct) return 0
    return Math.round(
      (1 - yearlyProduct.price / (monthlyProduct.price * 12)) * 100,
    )
  }, [yearlyProduct, monthlyProduct])

  const trialDays = yearlyProduct?.introPricing?.period ?? 7

  const [plan, setPlan] = useState<"yearly" | "monthly">("yearly")
  const [launching, setLaunching] = useState(false)
  const [showClose, setShowClose] = useState(false)
  const [count, setCount] = useState(0)
  const closeTimer = useRef<number | undefined>(undefined)
  const rafRef = useRef<number | undefined>(undefined)

  // Target for the live "memories secured" counter — a believable library.
  const TARGET = 4128

  useEffect(() => {
    if (reduce) {
      setShowClose(true)
      setCount(TARGET)
      return
    }

    // Feature paywalls shouldn't feel like a trap: reveal dismiss quickly, but
    // let the first lift-off land so the value registers.
    closeTimer.current = window.setTimeout(() => setShowClose(true), 1100)

    // Count up the secured tally with an ease-out so it decelerates like a
    // real sync settling.
    const start = performance.now()
    const DUR = 2400
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / DUR)
      const eased = 1 - Math.pow(1 - t, 3)
      setCount(Math.round(eased * TARGET))
      if (t < 1) rafRef.current = requestAnimationFrame(tick)
    }
    rafRef.current = requestAnimationFrame(tick)

    return () => {
      window.clearTimeout(closeTimer.current)
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
  }, [reduce])

  const handleClose = () => native.postNativeEvent("close")

  const handleSubscribe = () => {
    const p = plan === "yearly" ? yearlyProduct : monthlyProduct
    setLaunching(true)
    window.setTimeout(
      () => {
        if (p) native.subscribe(p)
      },
      reduce ? 0 : 620,
    )
  }

  const ctaLabel =
    plan === "yearly"
      ? `Turn on Backup — ${trialDays} days free`
      : `Turn on Backup — ${monthlyPrice}/mo`

  const ctaSub =
    plan === "yearly"
      ? `Then ${yearlyPrice}/year (just ${yearlyPerMonth}/mo). Cancel anytime.`
      : `Billed ${monthlyPrice} every month. Cancel anytime.`

  // Ring geometry (SVG). Drawn as a nearly-complete arc to read "securing".
  const R = 132
  const C = 2 * Math.PI * R

  return (
    <div className={styles.root}>
      {/* ── Above-the-clouds ambience ──────────────────────────────────── */}
      <div className={styles.sky} aria-hidden>
        <span className={styles.bloom} />
        <span className={`${styles.cloudLayer} ${styles.cloudLayerA}`} />
        <span className={`${styles.cloudLayer} ${styles.cloudLayerB}`} />
        <div className={styles.motes}>
          {Array.from({ length: 16 }).map((_, i) => (
            <span key={i} style={{ ["--i" as any]: i }} />
          ))}
        </div>
      </div>

      <div className={styles.scroll}>
        {/* ── Top bar: feature tag + delayed dismiss ───────────────────── */}
        <div className={styles.topbar}>
          <span className={styles.tag}>
            <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden>
              <path
                d="M7 18a4 4 0 0 1-.5-7.97 5.5 5.5 0 0 1 10.6-1.36A3.75 3.75 0 0 1 17.5 18H7z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinejoin="round"
              />
            </svg>
            Cloud Backup
          </span>
          <button
            className={`${styles.close} ${showClose ? styles.closeOn : ""}`}
            onClick={handleClose}
            aria-label="Not now"
            tabIndex={showClose ? 0 : -1}
          >
            <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden>
              <path
                d="M6 6l12 12M18 6L6 18"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {/* ── The stage: tray → column of light → cloud ────────────────── */}
        <div className={styles.stage}>
          {/* the protected cloud */}
          <div className={styles.cloud}>
            <div className={styles.cloudGlow} aria-hidden />
            <svg
              className={styles.ring}
              viewBox="0 0 300 300"
              aria-hidden
            >
              <defs>
                <linearGradient id="nb-ring" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0" stopColor="#ffd9a8" />
                  <stop offset="0.5" stopColor="#7fb2ff" />
                  <stop offset="1" stopColor="#2b5ff5" />
                </linearGradient>
              </defs>
              <circle
                cx="150"
                cy="150"
                r={R}
                fill="none"
                stroke="rgba(255,255,255,0.10)"
                strokeWidth="6"
              />
              <circle
                className={styles.ringFill}
                cx="150"
                cy="150"
                r={R}
                fill="none"
                stroke="url(#nb-ring)"
                strokeWidth="6"
                strokeLinecap="round"
                strokeDasharray={C}
                strokeDashoffset={C * 0.06}
                transform="rotate(-90 150 150)"
              />
            </svg>

            <div className={styles.cloudCore}>
              <svg
                className={styles.cloudMark}
                viewBox="0 0 64 64"
                aria-hidden
              >
                <path
                  d="M20 44a11 11 0 0 1-1.4-21.9 15 15 0 0 1 28.9-3.7A10.5 10.5 0 0 1 45 44H20z"
                  fill="url(#nb-ring)"
                  fillOpacity="0.16"
                  stroke="url(#nb-ring)"
                  strokeWidth="2"
                  strokeLinejoin="round"
                />
                <path
                  d="M26.5 31.5v-3a5.5 5.5 0 0 1 11 0v3"
                  fill="none"
                  stroke="#fff"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <rect
                  x="24"
                  y="31.5"
                  width="16"
                  height="11"
                  rx="2.5"
                  fill="#fff"
                  fillOpacity="0.92"
                />
                <circle cx="32" cy="37" r="1.9" fill="#2b5ff5" />
              </svg>
              <div className={styles.counter}>
                <span className={styles.countNum}>
                  {count.toLocaleString("en-US")}
                </span>
                <span className={styles.countLabel}>memories secured</span>
              </div>
            </div>
          </div>

          {/* the column of light + rising tiles */}
          <div className={styles.column} aria-hidden>
            <span className={styles.beamLight} />
            {FLYERS.map((f, i) => (
              <span
                className={styles.flyer}
                key={i}
                style={{
                  ["--x0" as any]: `${f.x0}px`,
                  ["--x1" as any]: `${f.x1}px`,
                  ["--d" as any]: `${f.d}s`,
                  ["--dur" as any]: `${f.dur}s`,
                }}
              >
                <img src={mem(f.n)} alt="" loading="lazy" />
                <span className={styles.flyerCheck}>
                  <svg viewBox="0 0 24 24" width="11" height="11">
                    <path
                      d="M5 12.5l4.2 4.2L19 7"
                      fill="none"
                      stroke="#fff"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </span>
            ))}
          </div>

          {/* the device tray — the camera roll */}
          <div className={styles.tray} aria-hidden>
            <span className={styles.scan} />
            <div className={styles.trayGrid}>
              {TRAY.map((n, i) => (
                <span
                  className={styles.trayTile}
                  key={i}
                  style={{ ["--i" as any]: i }}
                >
                  <img src={mem(n)} alt="" loading="lazy" />
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* ── Headline ─────────────────────────────────────────────────── */}
        <header className={styles.hero}>
          <p className={styles.eyebrow}>Nimbus Cloud Backup</p>
          <h1 className={styles.title}>
            Your whole library,
            <br />
            <span className={styles.titleGrad}>safe the second you shoot.</span>
          </h1>
          <p className={styles.sub}>
            Nimbus keeps an encrypted second copy of every photo and video off
            your phone. Drop it, lose it, upgrade it — every memory is already
            waiting for you on the next screen you open.
          </p>
        </header>

        {/* ── What "on" unlocks (one feature, four facets) ─────────────── */}
        <ul className={styles.features}>
          {FEATURES.map((f, i) => (
            <li
              className={styles.feature}
              key={f.key}
              style={{
                ["--c" as any]: f.color,
                ["--d" as any]: `${120 + i * 80}ms`,
              }}
            >
              <span className={styles.featIcon}>
                <f.Icon className={styles.featGlyph} />
              </span>
              <span className={styles.featText}>
                <span className={styles.featTitle}>{f.title}</span>
                <span className={styles.featDesc}>{f.desc}</span>
              </span>
            </li>
          ))}
        </ul>

        {/* ── Trust ────────────────────────────────────────────────────── */}
        <div className={styles.trust}>
          <div className={styles.stars} aria-hidden>
            {Array.from({ length: 5 }).map((_, i) => (
              <svg key={i} viewBox="0 0 24 24" width="15" height="15">
                <path
                  d="M12 2.6l2.7 5.9 6.4.7-4.8 4.3 1.3 6.3L12 17l-5.6 2.8 1.3-6.3-4.8-4.3 6.4-.7z"
                  fill="currentColor"
                />
              </svg>
            ))}
          </div>
          <p>
            <strong>4.9</strong> · 6M+ libraries backed up ·{" "}
            <span className={styles.enc}>256-bit encryption</span>
          </p>
        </div>

        {/* ── Trial timeline (trust + compliance) ──────────────────────── */}
        <section
          className={styles.timeline}
          aria-label="How your free trial works"
        >
          <h2 className={styles.timelineHead}>
            Your {trialDays}-day free trial, start to finish
          </h2>
          <ol className={styles.steps}>
            <li className={styles.step}>
              <span className={styles.stepDot} data-i="0" aria-hidden>
                <svg viewBox="0 0 24 24" width="14" height="14">
                  <path
                    d="M12 3a6 6 0 0 1 6 6c0 4-6 9-6 9s-6-5-6-9a6 6 0 0 1 6-6z"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M9.4 9.2l1.8 1.8 3.4-3.4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <span className={styles.stepBody}>
                <b>Today</b>
                <span>
                  Backup switches on. Your entire library begins copying to your
                  private cloud.
                </span>
              </span>
            </li>
            <li className={styles.step}>
              <span className={styles.stepDot} data-i="1" aria-hidden>
                <svg viewBox="0 0 24 24" width="14" height="14">
                  <path
                    d="M7 11V8a5 5 0 0 1 9.5-2"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                  />
                  <rect
                    x="5"
                    y="11"
                    width="14"
                    height="9"
                    rx="2"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                  />
                </svg>
              </span>
              <span className={styles.stepBody}>
                <b>Day {Math.max(1, trialDays - 2)}</b>
                <span>
                  We'll remind you before anything is charged. No silent
                  renewals, ever.
                </span>
              </span>
            </li>
            <li className={styles.step}>
              <span className={styles.stepDot} data-i="2" aria-hidden>
                <svg viewBox="0 0 24 24" width="14" height="14">
                  <path
                    d="M12 3.2l2.5 5.6 6 .6-4.5 4 1.3 5.9L12 16.9 6.7 19.3 8 13.4 3.5 9.4l6-.6z"
                    fill="currentColor"
                  />
                </svg>
              </span>
              <span className={styles.stepBody}>
                <b>Day {trialDays}</b>
                <span>
                  Your yearly plan begins — just {yearlyPerMonth}/mo, billed
                  once a year. Cancel any time before.
                </span>
              </span>
            </li>
          </ol>
        </section>

        {/* ── Pricing — yearly is the hero ─────────────────────────────── */}
        <section className={styles.pricing} aria-label="Choose your plan">
          <button
            className={`${styles.plan} ${styles.planYear} ${
              plan === "yearly" ? styles.planOn : ""
            }`}
            onClick={() => setPlan("yearly")}
            aria-pressed={plan === "yearly"}
          >
            <span className={styles.planGlow} aria-hidden />
            {savings > 0 && (
              <span className={styles.bestTag}>
                Best value · Save {savings}%
              </span>
            )}
            <span className={styles.planMain}>
              <span className={styles.planName}>Yearly</span>
              <span className={styles.radio} aria-hidden>
                <span className={styles.radioDot} />
              </span>
            </span>
            <span className={styles.priceRow}>
              <span className={styles.priceBig}>{yearlyPrice}</span>
              <span className={styles.pricePer}>/year</span>
            </span>
            <span className={styles.priceFoot}>
              just <b>{yearlyPerMonth}/mo</b> · {trialDays} days free
            </span>
          </button>

          <button
            className={`${styles.plan} ${styles.planMonth} ${
              plan === "monthly" ? styles.planOn : ""
            }`}
            onClick={() => setPlan("monthly")}
            aria-pressed={plan === "monthly"}
          >
            <span className={styles.planMain}>
              <span className={styles.planName}>Monthly</span>
              <span className={styles.radio} aria-hidden>
                <span className={styles.radioDot} />
              </span>
            </span>
            <span className={styles.priceRowSm}>
              <span className={styles.priceSm}>{monthlyPrice}</span>
              <span className={styles.pricePerSm}>/month</span>
            </span>
          </button>
        </section>

        {/* ── CTA ──────────────────────────────────────────────────────── */}
        <div className={styles.ctaWrap}>
          <button
            className={`${styles.cta} ${launching ? styles.ctaLaunch : ""}`}
            onClick={handleSubscribe}
          >
            <span className={styles.ctaSheen} aria-hidden />
            <span className={styles.ctaText}>{ctaLabel}</span>
            <svg
              className={styles.ctaArrow}
              viewBox="0 0 24 24"
              width="19"
              height="19"
              aria-hidden
            >
              <path
                d="M5 12h13M13 6l6 6-6 6"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <p className={styles.ctaSub}>{ctaSub}</p>
          <p className={styles.reassure}>
            Cloud Backup is a second copy — your originals never leave your
            phone. Cancel whenever you like and your whole library stays right
            where it is.
          </p>

          <div className={styles.legal}>
            <button onClick={() => native.postNativeEvent("already_purchased")}>
              Restore
            </button>
            <span>·</span>
            <button onClick={() => native.postNativeEvent("terms")}>
              Terms
            </button>
            <span>·</span>
            <button onClick={() => native.postNativeEvent("privacy")}>
              Privacy
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default NimbusPaywall
